import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Bot } from "lucide-react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { AssistantConversation } from "@/components/chat/AssistantConversation";

/**
 * Mobile AI assistant — floating pill/FAB + bottom sheet (TES-1128, WP4).
 *
 * DS-v2 reference: screen-09 "TP2 Assistant Mini-Bar" (a floating pill above
 * the bottom nav) and screen-06 "AI FAB + AI Bottom Sheet"
 * (`translate-y-full → translate-y-0`). Tapping the pill opens a bottom sheet
 * holding the shared conversation surface. Hidden on `lg+` (desktop uses the
 * docked panel instead). Sits above the 64px BottomNav.
 *
 * The project's stripped `sheet.tsx` only exports `Sheet` + `SheetContent`
 * (no `SheetHeader`/`SheetTitle`), so the header is built inline and the
 * required accessible title comes from `SheetPrimitive.Title` directly.
 */
export function AssistantMobile() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Floating pill (screen-09) — above BottomNav (h-16) */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("aiAssistant.open")}
        data-testid="assistant-fab"
        className={cn(
          "fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full bg-primary py-2 pl-4 pr-2",
          "text-on-primary shadow-lg border border-primary-container transition-transform active:scale-95 lg:hidden",
        )}
      >
        <span className="text-sm font-bold">{t("aiAssistant.title")}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-container text-on-secondary-container">
          <Bot className="h-4 w-4 animate-pulse" />
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          data-testid="assistant-sheet"
          className="flex h-[85vh] flex-col gap-0 rounded-t-xl p-0"
        >
          {/* Header — DS-v2 primary-container bar (SheetContent renders its own close button) */}
          <div className="flex items-center gap-2 border-b border-outline-variant bg-primary-container px-4 py-3 text-on-primary-container">
            <span className="relative">
              <Bot className="h-5 w-5 animate-pulse" />
              <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full border border-primary-container bg-secondary-container" />
            </span>
            <SheetPrimitive.Title className="font-display text-sm font-semibold text-on-primary-container">
              {t("aiAssistant.title")}
            </SheetPrimitive.Title>
          </div>

          <AssistantConversation className="flex min-h-0 flex-1 flex-col overflow-hidden" />
        </SheetContent>
      </Sheet>
    </>
  );
}
